import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import MainLayout from "../../components/layout/MainLayout";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Payroll.css";

function PayrollDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [viewMode, setViewMode] = useState("summary"); // summary | detailed

  const payrollId = params.payrollId || location.state?.payrollId;

  const fetchPayrollDetails = useCallback(async () => {
    try {
      const response = await api.get(`/payroll/details/${payrollId}`);

      if (response.data.success) {
        setPayrollData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch payroll details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load payroll details",
      }).then(() => {
        navigate("/payroll/list");
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, payrollId]);

  useEffect(() => {
    if (!payrollId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Payroll ID not provided",
      }).then(() => {
        navigate("/payroll/list");
      });
      return;
    }
    fetchPayrollDetails();
  }, [fetchPayrollDetails, navigate, payrollId]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const filteredEmployees = useMemo(() => {
    if (!payrollData?.employees) {
      return [];
    }

    let filtered = [...payrollData.employees];

    if (searchTerm) {
      const normalizedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((emp) => {
        const designationText =
          typeof emp.designation === "string"
            ? emp.designation
            : emp.designation?.designation_name || "";

        return (
          emp.name.toLowerCase().includes(normalizedSearch) ||
          emp.code.toLowerCase().includes(normalizedSearch) ||
          designationText.toLowerCase().includes(normalizedSearch)
        );
      });
    }

    if (filterDepartment) {
      filtered = filtered.filter((emp) => emp.department === filterDepartment);
    }

    return filtered;
  }, [filterDepartment, payrollData, searchTerm]);

  const departments = useMemo(() => {
    if (!payrollData?.employees) return [];
    const items = [...new Set(payrollData.employees.map((emp) => emp.department))];
    return items.filter(Boolean);
  }, [payrollData]);

  const buildPayslipFileName = (employee) => {
    const rawName = employee?.name || [employee?.first_name, employee?.last_name].filter(Boolean).join(" ");
    const safeName = (rawName || "Employee")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    const safeCode = (employee?.code || "EMP")
      .replace(/[^A-Za-z0-9_-]+/g, "_");

    return `Payslip_${safeName}_${safeCode}_${payrollData.month}_${payrollData.year}.pdf`;
  };

  const handleDownloadPayslip = async (employee) => {
    try {
      Swal.fire({
        title: "Generating Payslip",
        text: `Preparing payslip for ${employee.name}...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.get(
        `/payroll/download-payslip/${payrollId}/${employee.code}`,
        {
          responseType: "blob",
        },
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        buildPayslipFileName(employee),
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Payslip downloaded successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to download payslip:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to download payslip",
      });
    }
  };

  const handleDownloadAll = async () => {
    try {
      const result = await Swal.fire({
        title: "Download All Payslips?",
        text: `This will download ${filteredEmployees.length} payslips. Continue?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#6557ff",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, download all!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      Swal.fire({
        title: "Downloading Payslips",
        html: `Downloading <b>0</b> of <b>${filteredEmployees.length}</b> payslips...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < filteredEmployees.length; i++) {
        const employee = filteredEmployees[i];

        try {
          // Update progress
          Swal.update({
            html: `Downloading <b>${i + 1}</b> of <b>${filteredEmployees.length}</b> payslips...<br><small>${employee.name}</small>`,
          });

          const response = await api.get(
            `/payroll/download-payslip/${payrollId}/${employee.code}`,
            {
              responseType: "blob",
            },
          );

          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            buildPayslipFileName(employee),
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);

          successCount++;

          // Small delay to prevent overwhelming the browser
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          console.error(
            `Failed to download payslip for ${employee.name}:`,
            error,
          );
          failCount++;
        }
      }

      Swal.fire({
        icon: successCount > 0 ? "success" : "error",
        title: "Download Complete",
        html: `
                <div>
                    <p><strong>Success:</strong> ${successCount} payslips downloaded</p>
                    ${failCount > 0 ? `<p><strong>Failed:</strong> ${failCount} payslips</p>` : ""}
                </div>
            `,
        confirmButtonColor: "#6557ff",
      });
    } catch (error) {
      console.error("Failed to download payslips:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to download payslips",
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading payroll details...</p>
        </div>
      </MainLayout>
    );
  }

  if (!payrollData) {
    return (
      <MainLayout>
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>Payroll data not found</p>
          <button
            className="btn-primary"
            onClick={() => navigate("/payroll/list")}
          >
            Back to Payroll List
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="payroll-details-page">
        {/* Header */}
        <div className="page-header-section">
          <div>
            <div className="breadcrumb">
              <span
                onClick={() => navigate("/payroll/list")}
                className="breadcrumb-link"
              >
                Payroll
              </span>
              <i className="fas fa-chevron-right"></i>
              <span>Details</span>
            </div>
            <h4>
              Payroll Details - {payrollData.month} {payrollData.year}
            </h4>
            <p className="subtitle">
              {(payrollData.run_sequence && payrollData.run_sequence > 1)
                ? `Run ${payrollData.run_sequence}`
                : "Primary run"}{" "}
              •{" "}
              {payrollData.engine_version_label ||
                (payrollData.payroll_engine_version_id
                  ? `Version ${payrollData.payroll_engine_version_id}`
                  : "Legacy version")}{" "}
              • {payrollData.summary.total_employees} employees • Processed on{" "}
              {new Date(payrollData.created_at).toLocaleDateString("en-IN")}
            </p>
          </div>
          <div className="header-actions">
            <button
              className="btn-secondary"
              onClick={() => navigate("/payroll/list")}
            >
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button className="btn-primary" onClick={handleDownloadAll}>
              <i className="fas fa-download"></i> Download All
            </button>
          </div>

        </div>
        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon primary">
              <i className="fas fa-users"></i>
            </div>
            <div className="card-content">
              <h3>{payrollData.summary.total_employees}</h3>
              <p>Total Employees</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon success">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="card-content">
              <h3>Rs {formatNumber(payrollData.summary.total_gross_pay)}</h3>
              <p>Total Gross Pay</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon warning">
              <i className="fas fa-calculator"></i>
            </div>
            <div className="card-content">
              <h3>
                Rs{" "}
                {formatNumber(
                  payrollData.summary.total_pf +
                    payrollData.summary.total_esi +
                    payrollData.summary.total_tds +
                    payrollData.summary.total_prof_tax,
                )}
              </h3>
              <p>Total Deductions</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon danger">
              <i className="fas fa-wallet"></i>
            </div>
            <div className="card-content">
              <h3>Rs {formatNumber(payrollData.summary.total_net_pay)}</h3>
              <p>Total Net Pay</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search by name, code, or designation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
          >
            <option value="">All Departments</option>
            {departments.map((dept, index) => (
              <option key={index} value={dept}>
                {dept}
              </option>
            ))}
          </select>

          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === "summary" ? "active" : ""}`}
              onClick={() => setViewMode("summary")}
            >
              <i className="fas fa-list"></i> Summary
            </button>
            <button
              className={`toggle-btn ${viewMode === "detailed" ? "active" : ""}`}
              onClick={() => setViewMode("detailed")}
            >
              <i className="fas fa-table"></i> Detailed
            </button>
          </div>
        </div>

        {/* Employee Table */}
        <div className="table-card">
          {viewMode === "summary" ? (
            <SummaryTable
              employees={filteredEmployees}
              formatNumber={formatNumber}
              handleDownloadPayslip={handleDownloadPayslip}
            />
          ) : (
            <DetailedTable
              employees={filteredEmployees}
              formatNumber={formatNumber}
              handleDownloadPayslip={handleDownloadPayslip}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
}

// Summary Table Component
function SummaryTable({ employees, formatNumber, handleDownloadPayslip }) {
  return (
    <div className="table-responsive">
      <table className="payroll-table">
        <thead>
          <tr>
            <th>Sr. No.</th>
            <th>Employee</th>
            <th>Code</th>
            <th>Designation</th>
            <th>Department</th>
            <th className="text-right">Gross Pay</th>
            <th className="text-right">Effective Gross</th>
            <th className="text-right">Deductions</th>
            <th className="text-right">Net Pay</th>
            <th className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp, index) => {
            const totalDeductions =
              emp.pf + emp.esi + emp.tds + emp.prof_tax;
            return (
              <tr key={emp.id || index}>
                <td>{index + 1}</td>
                <td>
                  <div className="employee-info">
                    <div className="employee-avatar">
                      {emp.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="employee-name">{emp.name}</span>
                  </div>
                </td>
                <td>
                  <span className="badge badge-secondary">{emp.code}</span>
                </td>
                <td>{typeof emp.designation === "string" ? emp.designation : emp.designation?.designation_name || "N/A"}</td>
                <td>{emp.department || "N/A"}</td>
                <td className="text-right">Rs {formatNumber(emp.gross_pay)}</td>
                <td className="text-right">
                  Rs {formatNumber(emp.effective_gross)}
                </td>
                <td className="text-right text-danger">
                  Rs {formatNumber(totalDeductions)}
                </td>
                <td className="text-right net-pay">
                  Rs {formatNumber(emp.net_pay)}
                </td>
                <td className="text-center">
                  <button
                    className="action-btn download-btn"
                    onClick={() => handleDownloadPayslip(emp)}
                    title="Download Payslip"
                  >
                    <i className="fas fa-download"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Detailed Table Component
function DetailedTable({ employees, formatNumber, handleDownloadPayslip }) {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (employeeKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [employeeKey]: !prev[employeeKey],
    }));
  };

  return (
    <div className="table-responsive">
      <table className="payroll-table detailed">
        <thead>
          <tr>
            <th rowSpan="2">Sr.</th>
            <th rowSpan="2">Employee</th>
            <th rowSpan="2">Code</th>
            <th colSpan="5" className="text-center earnings-header">
              Earnings
            </th>
            <th colSpan="5" className="text-center deductions-header">
              Deductions
            </th>
            <th rowSpan="2" className="text-right">
              Net Pay
            </th>
            <th rowSpan="2" className="text-center">
              Action
            </th>
          </tr>
          <tr>
            <th className="text-right">Basic</th>
            <th className="text-right">HRA</th>
            <th className="text-right">Allowances</th>
            <th className="text-right">Gross</th>
            <th className="text-right">Eff. Gross</th>
            <th className="text-right">PF</th>
            <th className="text-right">ESI</th>
            <th className="text-right">TDS</th>
            <th className="text-right">Prof. Tax</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp, index) => {
            const employeeKey = emp.id || `${emp.code}-${index}`;
            const isExpanded = !!expandedRows[employeeKey];

            return (
              <React.Fragment key={employeeKey}>
                <tr>
                  <td>{index + 1}</td>
                  <td>
                    <div className="employee-info">
                      <div className="employee-avatar">
                        {emp.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="employee-name">{emp.name}</div>
                        <div className="employee-designation">
                          {typeof emp.designation === "string" ? emp.designation : emp.designation?.designation_name || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-secondary">{emp.code}</span>
                  </td>
                  <td className="text-right">Rs {formatNumber(emp.basic)}</td>
                  <td className="text-right">Rs {formatNumber(emp.hra)}</td>
                  <td className="text-right">
                    Rs {formatNumber(emp.total_allowances)}
                  </td>
                  <td className="text-right earnings-cell">
                    Rs {formatNumber(emp.gross_pay)}
                  </td>
                  <td className="text-right">
                    Rs {formatNumber(emp.effective_gross)}
                  </td>
                  <td className="text-right">Rs {formatNumber(emp.pf)}</td>
                  <td className="text-right">Rs {formatNumber(emp.esi)}</td>
                  <td className="text-right">Rs {formatNumber(emp.tds)}</td>
                  <td className="text-right">Rs {formatNumber(emp.prof_tax)}</td>
                  <td className="text-right net-pay">
                    Rs {formatNumber(emp.net_pay)}
                  </td>
                  <td className="text-center">
                    <div className="detail-actions">
                      <button
                        className="action-btn view-btn"
                        onClick={() => toggleRow(employeeKey)}
                        title={isExpanded ? "Hide HRA & Tax Details" : "Show HRA & Tax Details"}
                      >
                        <i className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                      </button>
                      <button
                        className="action-btn download-btn"
                        onClick={() => handleDownloadPayslip(emp)}
                        title="Download Payslip"
                      >
                        <i className="fas fa-download"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="detail-row">
                    <td colSpan="15">
                      <div className="detail-grid">
                        <div className="detail-pill">
                          <span className="detail-label">Metro Status</span>
                          <strong>{emp.is_metro_used ? "Metro" : "Non-Metro"}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Rent Paid / Month</span>
                          <strong>Rs {formatNumber(emp.rent_paid_monthly_used)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Salary For HRA / Month</span>
                          <strong>Rs {formatNumber(emp.salary_for_hra_monthly)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">HRA Received / Month</span>
                          <strong>Rs {formatNumber(emp.hra_received_monthly_used)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">HRA Exempt / Month</span>
                          <strong>Rs {formatNumber(emp.hra_exempt_monthly)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">HRA Taxable / Month</span>
                          <strong>Rs {formatNumber(emp.hra_taxable_monthly)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">HRA Exempt / Year</span>
                          <strong>Rs {formatNumber(emp.hra_exempt_yearly)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Rule 2</span>
                          <strong>Rs {formatNumber(emp.hra_rule_2_rent_minus_10_percent)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Rule 3 Cap</span>
                          <strong>Rs {formatNumber(emp.hra_rule_3_salary_percent_cap)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Surcharge Rate</span>
                          <strong>{formatNumber(emp.surcharge_rate_used)}%</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Surcharge Amount</span>
                          <strong>Rs {formatNumber(emp.surcharge_amount)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Marginal Relief</span>
                          <strong>Rs {formatNumber(emp.marginal_relief_amount)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Tax Before Cess</span>
                          <strong>Rs {formatNumber(emp.tax_before_cess_used)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Final Tax Liability</span>
                          <strong>Rs {formatNumber(emp.final_tax_liability)}</strong>
                        </div>
                        <div className="detail-pill">
                          <span className="detail-label">Education Cess</span>
                          <strong>Rs {formatNumber(emp.education_cess)}</strong>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PayrollDetails;



